import React from 'react';
import { motion } from 'framer-motion';
import { 
  Zap, 
  Wrench, 
  Users, 
  FileText, 
  Package, 
  Mail
} from 'lucide-react';

const FeaturesSection = () => {
  const features = [
    {
      icon: Zap,
      title: 'Advanced Price Intelligence',
      description: '10 pricing algorithms analyze live eBay data. Compare across eBay, Swappa, Back Market, Amazon, Apple Trade In & GameStop.',
      stats: '186+ listings analyzed per search',
      color: 'green',
      size: 'large',
    },
    {
      icon: Wrench,
      title: 'Repair Hub',
      description: 'Complete repair management with ticket creation, technician assignment, status workflow, and warranty tracking.',
      color: 'purple',
      size: 'tall',
    },
    {
      icon: Users,
      title: 'Customer Management',
      description: 'Full CRM with customer history, contact info, repair records, and purchase history.',
      color: 'blue',
      size: 'normal',
    },
    {
      icon: FileText,
      title: 'Digital Waivers',
      description: 'Paperless intake forms with signature capture and secure document storage.',
      color: 'purple',
      size: 'normal',
    },
    {
      icon: Package,
      title: 'Parts Inventory',
      description: 'Track parts stock, manage orders, and integrate with your repair workflow.',
      color: 'orange',
      size: 'normal',
    },
    {
      icon: Mail,
      title: 'Email Notifications',
      description: 'Beautiful HTML templates for repair status, pickup alerts, and review requests. Gmail & Outlook integration.',
      color: 'blue',
      size: 'normal',
    },
  ];

  const colorClasses = {
    green: 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20',
    purple: 'bg-primary/10 text-primary border-primary/20',
    blue: 'bg-[#6366F1]/10 text-[#6366F1] border-[#6366F1]/20',
    orange: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <section id="features" className="py-16 sm:py-24 md:py-32 relative" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-left mb-10 sm:mb-16"
        >
          <span className="text-xs sm:text-sm font-mono tracking-widest uppercase text-primary mb-3 sm:mb-4 block">
            Features
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4 sm:mb-6">
            Everything You Need to
            <br className="hidden sm:block" />
            <span className="text-gradient"> Maximize Profits</span>
          </h2>
          <p className="text-base sm:text-lg text-slate-400 max-w-2xl">
            From instant pricing to complete repair management, 2A Valuence is your all-in-one solution 
            for running a smarter electronics business.
          </p>
        </motion.div>

        {/* Mobile: Stack layout, Tablet: 2 cols, Desktop: Bento Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6"
        >
          {/* Large Feature Card - Price Intelligence */}
          <motion.div
            variants={itemVariants}
            className="feature-card sm:col-span-2 lg:row-span-2 bg-[#1A1A2E] border border-white/5 rounded-xl sm:rounded-2xl p-5 sm:p-8 hover:border-[#10B981]/30 transition-all duration-300 group"
            data-testid="feature-card-price-intelligence"
          >
            <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl ${colorClasses.green} flex items-center justify-center mb-4 sm:mb-6 border`}>
              <Zap className="w-5 h-5 sm:w-7 sm:h-7" />
            </div>
            <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-3 sm:mb-4">Advanced Price Intelligence</h3>
            <p className="text-slate-400 mb-4 sm:mb-6 text-sm sm:text-lg leading-relaxed">
              10 pricing algorithms analyze live eBay data. Compare prices across eBay, Swappa, Back Market, 
              Amazon, Apple Trade In, and GameStop. Platform fee calculations included.
            </p>
            <div className="flex items-center gap-3 mb-4 sm:mb-6">
              <div className="flex items-center gap-2 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full bg-[#10B981]/10 border border-[#10B981]/20">
                <span className="text-xs sm:text-sm text-[#10B981] font-medium">186+ listings analyzed</span>
              </div>
            </div>
            {/* Mini app preview - hidden on smallest screens */}
            <div className="relative mt-4 overflow-hidden rounded-lg sm:rounded-xl border border-white/10 hidden sm:block">
              <img
                src="https://customer-assets.emergentagent.com/job_device-valuation/artifacts/831u6kjc_IMG_3587_1242x2688.jpeg"
                alt="Price Intelligence Preview"
                className="w-full h-32 sm:h-48 object-cover object-top opacity-80 group-hover:opacity-100 transition-opacity duration-300"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A2E] to-transparent" />
            </div>
          </motion.div>

          {/* Repair Hub - Tall Card */}
          <motion.div
            variants={itemVariants}
            className="feature-card lg:row-span-2 bg-[#1A1A2E] border border-white/5 rounded-xl sm:rounded-2xl p-5 sm:p-6 hover:border-primary/30 transition-all duration-300"
            data-testid="feature-card-repair-hub"
          >
            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl ${colorClasses.purple} flex items-center justify-center mb-3 sm:mb-4 border`}>
              <Wrench className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3">Repair Hub</h3>
            <p className="text-slate-400 text-xs sm:text-sm mb-3 sm:mb-4">
              Complete repair management with ticket creation, technician assignment, and automated workflows.
            </p>
            <div className="space-y-1.5 sm:space-y-2 mb-4">
              {['Ticket Management', 'Technician Assignment', 'Auto Notifications', 'Warranty Tracking'].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-xs sm:text-sm text-slate-300">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                  {item}
                </div>
              ))}
            </div>
            <div className="mt-auto hidden sm:block">
              <img
                src="https://customer-assets.emergentagent.com/job_device-valuation/artifacts/q2p05fdl_IMG_3586_1242x2688.jpeg"
                alt="Repair Hub Preview"
                className="w-full h-24 sm:h-32 object-cover object-top rounded-lg opacity-70"
              />
            </div>
          </motion.div>

          {/* Customer Management */}
          <motion.div
            variants={itemVariants}
            className="feature-card bg-[#1A1A2E] border border-white/5 rounded-xl sm:rounded-2xl p-5 sm:p-6 hover:border-[#6366F1]/30 transition-all duration-300"
            data-testid="feature-card-crm"
          >
            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl ${colorClasses.blue} flex items-center justify-center mb-3 sm:mb-4 border`}>
              <Users className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <h3 className="text-base sm:text-lg font-bold text-white mb-2">Customer Management</h3>
            <p className="text-slate-400 text-xs sm:text-sm">
              Full CRM with customer history, contact info, and purchase records.
            </p>
          </motion.div>

          {/* Digital Waivers */}
          <motion.div
            variants={itemVariants}
            className="feature-card bg-[#1A1A2E] border border-white/5 rounded-xl sm:rounded-2xl p-5 sm:p-6 hover:border-primary/30 transition-all duration-300"
            data-testid="feature-card-waivers"
          >
            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl ${colorClasses.purple} flex items-center justify-center mb-3 sm:mb-4 border`}>
              <FileText className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <h3 className="text-base sm:text-lg font-bold text-white mb-2">Digital Waivers</h3>
            <p className="text-slate-400 text-xs sm:text-sm">
              Paperless intake forms with signature capture on device.
            </p>
          </motion.div>

          {/* Parts Inventory */}
          <motion.div
            variants={itemVariants}
            className="feature-card bg-[#1A1A2E] border border-white/5 rounded-xl sm:rounded-2xl p-5 sm:p-6 hover:border-orange-500/30 transition-all duration-300"
            data-testid="feature-card-inventory"
          >
            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl ${colorClasses.orange} flex items-center justify-center mb-3 sm:mb-4 border`}>
              <Package className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <h3 className="text-base sm:text-lg font-bold text-white mb-2">Parts Inventory</h3>
            <p className="text-slate-400 text-xs sm:text-sm">
              Track stock levels, manage orders, integrate with repairs.
            </p>
          </motion.div>

          {/* Email Notifications */}
          <motion.div
            variants={itemVariants}
            className="feature-card bg-[#1A1A2E] border border-white/5 rounded-xl sm:rounded-2xl p-5 sm:p-6 hover:border-[#6366F1]/30 transition-all duration-300"
            data-testid="feature-card-email"
          >
            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl ${colorClasses.blue} flex items-center justify-center mb-3 sm:mb-4 border`}>
              <Mail className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <h3 className="text-base sm:text-lg font-bold text-white mb-2">Email Notifications</h3>
            <p className="text-slate-400 text-xs sm:text-sm">
              Beautiful HTML templates. Gmail & Outlook OAuth integration.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturesSection;
